import React, { useEffect, useRef, useState, useCallback } from 'react';
import gsap from 'gsap';
import { Observer } from 'gsap/Observer';
import {
  SECTION_CHECKPOINTS,
  SECTION_LABELS,
  TOTAL_FRAMES,
  TRANSITION_DURATION,
  COOLDOWN_DURATION_MS,
  type ScrollLockedAvianSequenceProps,
  type FrameTrackState,
} from './avianSequence.types';
import { drawAvianFrame, startAvianPreload } from './avianPreloader';
import Hero from '../../features/hero/Hero';
import About from '../../features/about/About';
import Projects from '../../features/projects/Projects';
import Experience from '../../features/experience/Experience';
import Achievements from '../../features/achievements/Achievements';
import SequenceHUD from './SequenceHUD';
import { useSequenceHUDState } from './useSequenceHUDState';
import './ScrollLockedAvianSequence.styles.css';

gsap.registerPlugin(Observer);

/**
 * Discrete Scroll-Locked Canvas Sequence Engine (Desktop).
 * Renders high-DPI avian WebP frames synchronized with 5 core portfolio sections.
 */
export const ScrollLockedAvianSequence: React.FC<ScrollLockedAvianSequenceProps> = ({
  onSectionChange,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  const [activeSection, setActiveSection] = useState<number>(0);
  const isAnimatingRef = useRef<boolean>(false);
  const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentSectionRef = useRef<number>(0);
  const frameTrackRef = useRef<FrameTrackState>({ frame: 0 });
  const imagesRef = useRef<HTMLImageElement[]>([]);

  // * Unified HUD State (Recruiter Mode + Zoom Controls) across all 5 sections
  const {
    scale,
    isRecruiterActive,
    handleZoomIn,
    handleZoomOut,
    handleRecruiterToggle,
  } = useSequenceHUDState(false);

  const isRecruiterActiveRef = useRef<boolean>(isRecruiterActive);
  useEffect(() => {
    isRecruiterActiveRef.current = isRecruiterActive;
  }, [isRecruiterActive]);

  // * Repaints current frame using high-DPI aspect cover math with About offset
  const renderFrame = useCallback((frameIndex: number) => {
    drawAvianFrame(
      canvasRef.current,
      imagesRef.current,
      frameIndex,
      TOTAL_FRAMES,
      true
    );
  }, []);

  // * Canvas buffer sizing handler
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    renderFrame(Math.round(frameTrackRef.current.frame));
  }, [renderFrame]);

  // * Discrete section transition controller with Master GSAP Timeline & Directional Parallax
  const goToSection = useCallback(
    (targetIndex: number) => {
      const maxAllowed = isRecruiterActiveRef.current ? SECTION_CHECKPOINTS.length - 1 : 2;
      if (
        targetIndex < 0 ||
        targetIndex > maxAllowed ||
        isAnimatingRef.current ||
        targetIndex === currentSectionRef.current
      ) {
        return;
      }

      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
        cooldownTimeoutRef.current = null;
      }

      if (timelineRef.current) {
        timelineRef.current.kill();
        timelineRef.current = null;
      }

      isAnimatingRef.current = true;
      const fromIndex = currentSectionRef.current;
      const toIndex = targetIndex;
      const direction = toIndex > fromIndex ? 1 : -1;
      const fromEl = sectionRefs.current[fromIndex];
      const toEl = sectionRefs.current[toIndex];
      const targetFrame = SECTION_CHECKPOINTS[toIndex];
      const frameDelta = Math.abs(targetFrame - frameTrackRef.current.frame);

      // Sublinear scrub duration scaling for multi-section jumps
      const scrubDuration =
        frameDelta > 50
          ? Math.min(1.4, 1.15 + (frameDelta / 200) * 0.25)
          : TRANSITION_DURATION;

      // Ensure all intermediate sections stay completely hidden
      sectionRefs.current.forEach((el, idx) => {
        if (!el) return;
        if (idx !== fromIndex && idx !== toIndex) {
          gsap.set(el, { opacity: 0, pointerEvents: 'none', y: 0 });
        }
      });

      if (fromEl) {
        gsap.set(fromEl, { pointerEvents: 'none' });
      }
      if (toEl) {
        gsap.set(toEl, { opacity: 0, y: direction * 35, pointerEvents: 'none' });
      }

      const tl = gsap.timeline({
        onComplete: () => {
          currentSectionRef.current = toIndex;
          setActiveSection(toIndex);
          onSectionChange?.(toIndex);

          if (toEl) {
            gsap.set(toEl, { opacity: 1, y: 0, pointerEvents: 'auto' });
          }

          cooldownTimeoutRef.current = setTimeout(() => {
            isAnimatingRef.current = false;
            timelineRef.current = null;
          }, COOLDOWN_DURATION_MS);
        },
      });

      timelineRef.current = tl;

      // 1. Outgoing section vanishes rapidly with directional translation
      if (fromEl) {
        tl.to(
          fromEl,
          {
            opacity: 0,
            y: -direction * 35,
            duration: 0.3,
            ease: 'power2.in',
          },
          0
        );
      }

      // 2. Avian frame scrubs smoothly
      tl.to(
        frameTrackRef.current,
        {
          frame: targetFrame,
          duration: scrubDuration,
          ease: 'power2.inOut',
          onUpdate: () => {
            requestAnimationFrame(() => {
              renderFrame(Math.round(frameTrackRef.current.frame));
            });
          },
        },
        0.05
      );

      // 3. Incoming section enters and completes exactly as bird arrives
      if (toEl) {
        const entranceStart = Math.max(0.35, scrubDuration - 0.45);
        tl.to(
          toEl,
          {
            opacity: 1,
            y: 0,
            duration: 0.45,
            ease: 'power2.out',
          },
          entranceStart
        );
      }
    },
    [renderFrame, onSectionChange]
  );

  // * Initialize canvas resizing, section wrapper states, and progressive frame preloading
  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initial state setup for section wrappers: 0 is visible, others hidden
    sectionRefs.current.forEach((el, idx) => {
      if (!el) return;
      if (idx === 0) {
        gsap.set(el, { opacity: 1, y: 0, pointerEvents: 'auto' });
      } else {
        gsap.set(el, { opacity: 0, y: 0, pointerEvents: 'none' });
      }
    });

    const { images, cleanup } = startAvianPreload(
      '/sequenceDesktop',
      TOTAL_FRAMES,
      () => renderFrame(0)
    );
    imagesRef.current = images;

    return () => {
      cleanup();
      window.removeEventListener('resize', resizeCanvas);
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
      }
    };
  }, [renderFrame, resizeCanvas]);

  // * Auto-snap back to Projects if recruiter mode becomes disabled while on Experience or Achievements
  useEffect(() => {
    if (!isRecruiterActive && currentSectionRef.current > 2) {
      goToSection(2);
    }
  }, [isRecruiterActive, goToSection]);

  // * GSAP Observer for mouse wheel and touch gestures
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = Observer.create({
      target: containerRef.current,
      type: 'wheel,touch,pointer',
      wheelSpeed: -1,
      tolerance: 15,
      preventDefault: true,
      // * Isolate internal scrolling within Experience card and rail from full-page section flips
      ignoreCheck: (event: Event): boolean => {
        try {
          const target = event.target as HTMLElement | null;
          if (!target) return false;
          const scrollableEl = target.closest(
            '.experience-card-scrollable, .rail-pill-stack, .avian-section-wrapper'
          ) as HTMLElement | null;
          if (!scrollableEl) return false;

          // ? Check if element has scrollable overflow
          const hasOverflow = scrollableEl.scrollHeight > scrollableEl.clientHeight;
          if (!hasOverflow) return false;

          if ('deltaY' in event) {
            const wheelEvt = event as WheelEvent;
            const isDown = wheelEvt.deltaY > 0;
            const isUp = wheelEvt.deltaY < 0;
            const canScrollDown =
              scrollableEl.scrollTop + scrollableEl.clientHeight <
              scrollableEl.scrollHeight - 2;
            const canScrollUp = scrollableEl.scrollTop > 2;

            // ! Ignore full-page swipe if internal content can still scroll in this direction
            if ((isDown && canScrollDown) || (isUp && canScrollUp)) {
              return true;
            }
          }
          return false;
        } catch {
          return false;
        }
      },
      onDown: () => {
        if (!isAnimatingRef.current) {
          goToSection(currentSectionRef.current - 1);
        }
      },
      onUp: () => {
        if (!isAnimatingRef.current) {
          const maxAllowed = isRecruiterActiveRef.current ? SECTION_CHECKPOINTS.length - 1 : 2;
          if (currentSectionRef.current < maxAllowed) {
            goToSection(currentSectionRef.current + 1);
          }
        }
      },
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isAnimatingRef.current) return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        const maxAllowed = isRecruiterActiveRef.current ? SECTION_CHECKPOINTS.length - 1 : 2;
        if (currentSectionRef.current < maxAllowed) {
          goToSection(currentSectionRef.current + 1);
        }
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        goToSection(currentSectionRef.current - 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    const handleAnchorClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement)?.closest('a[href*="#"]');
      if (!target) return;
      const href = target.getAttribute('href');
      if (!href) return;
      const hash = href.split('#')[1]?.toLowerCase();
      if (!hash) return;

      const sectionIndexMap: Record<string, number> = {
        home: 0,
        hero: 0,
        about: 1,
        projects: 2,
        experience: 3,
        achievements: 4,
      };

      if (hash in sectionIndexMap) {
        const targetIdx = sectionIndexMap[hash];
        if (!isRecruiterActiveRef.current && targetIdx > 2) return;
        e.preventDefault();
        goToSection(targetIdx);
      }
    };

    const handleNavigateSection = (e: Event) => {
      const targetId = (e as CustomEvent)?.detail?.targetId?.toLowerCase();
      if (!targetId) return;

      const sectionIndexMap: Record<string, number> = {
        home: 0,
        hero: 0,
        about: 1,
        projects: 2,
        experience: 3,
        achievements: 4,
      };

      if (targetId in sectionIndexMap) {
        const targetIdx = sectionIndexMap[targetId];
        if (!isRecruiterActiveRef.current && targetIdx > 2) return;
        goToSection(targetIdx);
      }
    };

    document.addEventListener('click', handleAnchorClick, true);
    window.addEventListener('navigate-section', handleNavigateSection);

    return () => {
      observer.kill();
      document.removeEventListener('click', handleAnchorClick, true);
      window.removeEventListener('navigate-section', handleNavigateSection);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToSection]);

  return (
    <div
      ref={containerRef}
      className={`avian-sequence-container ${className}`}
      data-testid="avian-sequence-container"
    >
      {/* * Pinned Background WebP Canvas */}
      <canvas
        ref={canvasRef}
        className="avian-sequence-canvas"
        data-testid="avian-sequence-canvas"
      />

      {/* * Section 0: Hero Section Overlay */}
      <div
        ref={(el) => {
          sectionRefs.current[0] = el;
        }}
        className="avian-hero-wrapper"
      >
        <Hero
          isActive={activeSection === 0}
          scale={scale}
          isRecruiterActive={isRecruiterActive}
        />
      </div>

      {/* * Section 1: About Me Section Overlay */}
      <div
        ref={(el) => {
          sectionRefs.current[1] = el;
        }}
        className="avian-about-wrapper"
      >
        <About isActive={activeSection === 1} scale={scale} />
      </div>

      {/* * Section 2: Projects Showcase Overlay */}
      <div
        ref={(el) => {
          sectionRefs.current[2] = el;
        }}
        className="avian-section-wrapper"
      >
        <Projects isActive={activeSection === 2} scale={scale} />
      </div>

      {/* * Section 3: Experience Section Overlay */}
      <div
        ref={(el) => {
          sectionRefs.current[3] = el;
        }}
        className="avian-section-wrapper"
      >
        <Experience isActive={activeSection === 3} scale={scale} />
      </div>

      {/* * Section 4: Achievements Section Overlay */}
      <div
        ref={(el) => {
          sectionRefs.current[4] = el;
        }}
        className="avian-section-wrapper"
      >
        <Achievements isActive={activeSection === 4} scale={scale} />
      </div>

      {/* * Unified Sequence HUD: Recruiter Mode (Top-Right) & Zoom Controls (Bottom-Right) on all sections */}
      <SequenceHUD
        scale={scale}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        isRecruiterActive={isRecruiterActive}
        onRecruiterToggle={handleRecruiterToggle}
        showRecruiterTooltip={!isRecruiterActive && activeSection === 2}
      />

      {/* * Navigation Dots Indicator with Persistent and Hover Section Labels */}
      <nav className="avian-dots-nav" aria-label="Section sequence navigation">
        {SECTION_CHECKPOINTS.map((_, idx) => {
          if (!isRecruiterActive && idx > 2) {
            return null;
          }
          const isActive = activeSection === idx;
          const label = SECTION_LABELS[idx] || `Section ${idx + 1}`;
          return (
            <div
              key={idx}
              className={`avian-dot-item ${isActive ? 'is-active' : ''}`}
            >
              <button
                type="button"
                onClick={() => goToSection(idx)}
                className={`avian-dot-btn ${isActive ? 'is-active' : ''}`}
                aria-label={`Jump to ${label}`}
                aria-current={isActive ? 'true' : 'false'}
              />
              <span className={`avian-dot-label ${isActive ? 'is-active' : ''}`}>
                {label}
              </span>
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default ScrollLockedAvianSequence;
